import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TestTube, CheckCircle, XCircle, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import TestRunnerLauncher from '../components/TestRunnerLauncher';

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';
const fmtDuration = (s) => (s == null ? '—' : `${Number(s).toFixed(1)}s`);

function StatPill({ label, value, color = 'gray' }) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={`border rounded-md px-3 py-2 ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
      <div className="text-xl font-bold leading-tight">{value}</div>
    </div>
  );
}

export default function TestRunnerPage() {
  const { user } = useAuth();
  const isOwner = ['admin', 'owner'].includes(user?.role);
  const canView = ['admin', 'owner', 'accountant', 'assistant'].includes(user?.role);

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { api } = await import('../lib/api');
      const r = await api.get('/admin/test-runs?limit=15');
      const list = r.data?.runs || [];
      setRuns(list);
      if (list.length > 0) setLatest(list[0]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) loadHistory(); }, [canView, loadHistory]);

  // Refresh history after any launcher update
  const handleLauncherChange = useCallback((run) => {
    setLatest(run);
    if (run?.status !== 'running') {
      loadHistory();
    }
  }, [loadHistory]);

  if (!canView) {
    return (
      <div className="p-6" data-testid="test-runner-denied">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          Access denied — Owner / Admin / CA / Assistant only.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="test-runner-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TestTube size={24} weight="duotone" className="text-[#002FA7]" />
          <h1 className="text-2xl font-bold text-gray-900">Regression Tests</h1>
          <span className="text-xs text-gray-500 italic">Smoke suite (recovery + settings + security)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadHistory}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300"
            data-testid="refresh-test-runs-btn"
            title="Refresh history"
          >
            <ArrowsClockwise size={14} weight="bold" />
            Refresh
          </button>
          <TestRunnerLauncher canRun={isOwner} onChange={handleLauncherChange} />
        </div>
      </div>

      {latest && (
        <div
          className={`border rounded-lg p-5 ${
            latest.status === 'running'
              ? 'bg-blue-50 border-blue-200'
              : latest.success
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
          data-testid="latest-test-run"
        >
          <div className="flex items-center gap-3 mb-4">
            {latest.status === 'running' ? (
              <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            ) : latest.success ? (
              <CheckCircle size={28} weight="fill" className="text-green-600" />
            ) : (
              <XCircle size={28} weight="fill" className="text-red-600" />
            )}
            <div className="flex-1">
              <div className="text-lg font-bold text-gray-900">
                {latest.status === 'running'
                  ? 'Running tests in background…'
                  : latest.success
                  ? 'All Checks Passed'
                  : 'Failures Detected'}
              </div>
              <div className="text-xs text-gray-600">
                {fmtDateTime(latest.finished_at || latest.started_at)}
                {latest.triggered_by_name && <> • by <b>{latest.triggered_by_name}</b></>}
              </div>
            </div>
            <div className="text-2xl font-bold font-mono">
              {fmtDuration(latest.duration_sec)}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Passed" value={latest.passed || 0} color="green" />
            <StatPill label="Failed" value={latest.failed || 0} color={latest.failed ? 'red' : 'gray'} />
            <StatPill label="Errors" value={latest.errors || 0} color={latest.errors ? 'red' : 'gray'} />
            <StatPill label="Skipped" value={latest.skipped || 0} color="amber" />
          </div>

          {latest.failures && latest.failures.length > 0 && (
            <div className="mt-4 bg-white border border-red-200 rounded-md p-3" data-testid="latest-failures">
              <div className="flex items-center gap-1 mb-2 text-xs font-semibold text-red-800 uppercase tracking-wide">
                <Warning size={14} weight="fill" />
                Failures ({latest.failures.length})
              </div>
              <ul className="space-y-1">
                {latest.failures.map((f, i) => (
                  <li key={`${f.kind || 'F'}-${f.test || i}`} className="text-xs font-mono text-gray-800 break-all">
                    <span className="font-semibold text-red-700">{f.kind}</span>{' '}
                    {f.test}
                    {f.detail && <span className="text-gray-500"> — {f.detail}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {latest.output_tail && latest.status !== 'running' && (
            <details className="mt-3" data-testid="latest-output-tail">
              <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
                View raw output tail
              </summary>
              <pre className="mt-2 bg-gray-900 text-green-300 text-[11px] font-mono p-3 rounded-md overflow-x-auto max-h-72">
                {latest.output_tail}
              </pre>
            </details>
          )}
        </div>
      )}

      {!latest && !loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center" data-testid="empty-history">
          <TestTube size={36} weight="duotone" className="text-gray-400 mx-auto mb-2" />
          <div className="text-sm text-gray-600">
            Abhi tak koi test run nahi hua.
            {isOwner && ' Upar "Run Tests Now" click karo.'}
          </div>
        </div>
      )}

      {runs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="test-runs-history">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="font-semibold text-sm text-gray-900">Run History</div>
            <span className="text-xs text-gray-500">Last {runs.length} runs</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs uppercase">When</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs uppercase">By</th>
                  <th className="px-4 py-2 text-center font-semibold text-gray-700 text-xs uppercase">Status</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700 text-xs uppercase">Pass</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700 text-xs uppercase">Fail</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700 text-xs uppercase">Skip</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700 text-xs uppercase">Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`test-run-row-${r.id}`}>
                    <td className="px-4 py-2 text-xs font-mono text-gray-700">{fmtDateTime(r.finished_at || r.started_at)}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.triggered_by_name || '—'}</td>
                    <td className="px-4 py-2 text-center">
                      {r.status === 'running' ? (
                        <span className="inline-flex items-center gap-1 text-blue-700 text-xs font-semibold">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          RUNNING
                        </span>
                      ) : r.success ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
                          <CheckCircle size={14} weight="fill" /> PASS
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold">
                          <XCircle size={14} weight="fill" /> FAIL
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-right font-mono text-green-700 font-semibold">{r.passed || 0}</td>
                    <td className={`px-4 py-2 text-xs text-right font-mono ${r.failed ? 'text-red-700 font-semibold' : 'text-gray-400'}`}>{r.failed || 0}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono text-amber-700">{r.skipped || 0}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono text-gray-700">{fmtDuration(r.duration_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
